<template>
  <div class="elite-tabs-wrapper-content">
    <tabs
      fill
      class="flex-column flex-md-row"
      tabNavWrapperClasses="nav-wrapper"
      tabNavClasses="nav elite-tabs"
      value="templates"
    >
      <!-- Tab 1 : Liste des modèles -->
      <tab-pane title="templates" id="1" :active="true">
        <span slot="title">
          <i class="fal fa-envelope-open-text"></i>
          {{ $t("MAIL_V2.TEMPLATES_LIST") }}
        </span>

        <div class="elite-tabs-wrapper-content">
          <!-- Filters -->
          <div class="page-wrapper-table-header mb-3">
            <el-select
              v-model="filterCategory"
              :placeholder="$t('MAIL_V2.ALL_CATEGORIES')"
              clearable
            >
              <el-option value="transactional" :label="$t('MAIL_V2.TRANSACTIONAL')" />
              <el-option value="notification" :label="$t('MAIL_V2.NOTIFICATION')" />
              <el-option value="marketing" :label="$t('MAIL_V2.MARKETING')" />
            </el-select>
            <el-select
              v-model="filterStatus"
              :placeholder="$t('MAIL_V2.ALL_STATUS')"
              clearable
            >
              <el-option value="customized" :label="$t('MAIL_V2.CUSTOMIZED')" />
              <el-option value="default" :label="$t('MAIL_V2.USING_DEFAULT')" />
            </el-select>
            <base-input
              v-model="query"
              type="search"
              prepend-icon="fas fa-search"
              :placeholder="$t('COMMON.SEARCH')"
              clearable
            />
          </div>

          <!-- Templates cards -->
          <div class="mail-type-cards" v-loading="loading">
            <div v-for="item in filteredTemplates" :key="item.mail_type_id" class="mail-type-card">
              <div class="mail-type-card-header">
                <div class="mail-type-card-info">
                  <h5 class="mail-type-card-title">{{ getLocale(item.type_name) }}</h5>
                  <span class="mail-type-card-code">{{ item.code }}</span>
                </div>
                <div class="mail-type-card-badges">
                  <span class="badge-category" :class="'badge-category--' + item.category">
                    {{ $t("MAIL_V2." + item.category.toUpperCase()) }}
                  </span>
                  <span class="badge-status" :class="item.is_customized ? 'badge-status--custom' : 'badge-status--default'">
                    {{ item.is_customized ? $t("MAIL_V2.CUSTOMIZED") : $t("MAIL_V2.USING_DEFAULT") }}
                  </span>
                </div>
              </div>

              <div class="mail-type-card-locales">
                <span v-for="locale in item.available_locales" :key="locale" class="locale-chip">{{ locale.toUpperCase() }}</span>
              </div>

              <div class="mail-type-card-actions">
                <button v-if="!item.is_customized" class="btn btn-sm btn-outline-primary" @click="customize(item)">
                  <i class="fal fa-edit"></i> {{ $t("MAIL_V2.CUSTOMIZE") }}
                </button>
                <template v-else>
                  <button class="btn btn-sm btn-outline-primary" @click="editTemplate(item)">
                    <i class="fal fa-edit"></i> {{ $t("COMMON.EDIT") }}
                  </button>
                  <button class="btn btn-sm btn-outline-danger" @click="resetToDefault(item)">
                    <i class="fal fa-undo"></i> {{ $t("MAIL_V2.RESET_DEFAULT") }}
                  </button>
                </template>
              </div>
            </div>

            <div v-if="!loading && !filteredTemplates.length" class="text-center py-5 text-muted">
              {{ $t("MAIL_V2.NO_MAIL_TYPES") }}
            </div>
          </div>

          <!-- Edit Template Modal -->
          <div v-if="isEditModalOpened" class="resizable-wrapper show" :key="renderKey">
            <div class="resizable-wrapper-content w-100" v-resizable-modal="'MAIL_V2_EDIT'">
              <div class="resizable-wrapper-content-header">
                <div class="resizable-wrapper-content-header-left">
                  <h1>{{ $t("MAIL_V2.EDIT_TEMPLATE") }} — {{ getLocale(editingItem?.type_name) }}</h1>
                </div>
                <div class="resizable-wrapper-content-header-right">
                  <button class="close" @click="closeModal"><i class="fal fa-times"></i></button>
                </div>
              </div>
              <div class="resizable-wrapper-content-body mv2-editor-body">
                <mail-v2-editor
                  v-if="editingItem && editingTemplate"
                  :template-data="editingTemplate"
                  :mail-type="editingItem"
                  :organization-id="organizationId"
                  :organization="organization"
                  @saved="onSaved"
                  @cancel="closeModal"
                />
              </div>
            </div>
          </div>
        </div>
      </tab-pane>

      <!-- Tab 2 : Paramètres mail -->
      <tab-pane title="settings" id="2">
        <span slot="title">
          <i class="fal fa-cog"></i>
          {{ $t("MAIL_V2.MAIL_SETTINGS") }}
        </span>

        <div class="elite-tabs-wrapper-content">
          <form class="add-form" @submit.prevent="saveSettings" @keydown.enter.prevent="() => {}" v-loading="settingsLoading">

            <!-- Expéditeur -->
            <div class="col-12 mt-2 mb-3">
              <h2>{{ $t("MAIL_V2.SENDER_DEFAULTS") }}</h2>
            </div>

            <div class="form-wrapper">
              <base-input
                :label="$t('MAIL_V2.FROM_NAME')"
                :placeholder="$t('MAIL_V2.FROM_NAME')"
                v-model="settingsForm.from_name"
              />
            </div>

            <div class="form-wrapper">
              <base-input
                :label="$t('MAIL_V2.FROM_EMAIL')"
                :placeholder="$t('MAIL_V2.FROM_EMAIL')"
                v-model="settingsForm.from_email"
              />
            </div>

            <div class="form-wrapper">
              <base-input
                :label="$t('MAIL_V2.REPLY_TO_NAME')"
                :placeholder="$t('MAIL_V2.REPLY_TO_NAME')"
                v-model="settingsForm.reply_to_name"
              />
            </div>

            <div class="form-wrapper">
              <base-input
                :label="$t('MAIL_V2.REPLY_TO_EMAIL')"
                :placeholder="$t('MAIL_V2.REPLY_TO_EMAIL')"
                v-model="settingsForm.reply_to_email"
              />
            </div>

            <div class="form-wrapper">
              <base-input
                :label="$t('MAIL_V2.DEFAULT_LOCALE')"
                :placeholder="$t('MAIL_V2.DEFAULT_LOCALE')"
              >
                <el-select v-model="settingsForm.default_locale">
                  <el-option value="fr" label="Français" />
                  <el-option value="en" label="English" />
                </el-select>
              </base-input>
            </div>

            <!-- Identité visuelle -->
            <div class="col-12 mt-2 mb-3">
              <h2>{{ $t("MAIL_V2.BRANDING") }}</h2>
            </div>

            <div class="form-wrapper full">
              <base-input
                :label="$t('MAIL_V2.LOGO_URL')"
                :placeholder="'https://...'"
                v-model="settingsForm.logo_url"
              />
            </div>

            <div class="form-wrapper">
              <base-input
                :label="$t('MAIL_V2.LOGO_HEIGHT')"
                :placeholder="'40'"
                v-model.number="settingsForm.logo_height"
                type="number"
              />
            </div>

            <div class="form-wrapper">
              <base-input :label="$t('MAIL_V2.PRIMARY_COLOR')">
                <div class="d-flex align-items-center">
                  <input type="color" v-model="settingsForm.brand_primary_color" style="width:40px;height:34px;border:2px solid #dee2e6;border-radius:6px;cursor:pointer;padding:2px;" />
                  <span class="ml-2 text-muted" style="font-size:12px;">{{ settingsForm.brand_primary_color }}</span>
                </div>
              </base-input>
            </div>

            <div class="form-wrapper">
              <base-input :label="$t('MAIL_V2.SECONDARY_COLOR')">
                <div class="d-flex align-items-center">
                  <input type="color" v-model="settingsForm.brand_secondary_color" style="width:40px;height:34px;border:2px solid #dee2e6;border-radius:6px;cursor:pointer;padding:2px;" />
                  <span class="ml-2 text-muted" style="font-size:12px;">{{ settingsForm.brand_secondary_color }}</span>
                </div>
              </base-input>
            </div>

            <!-- Pied de page -->
            <div class="col-12 mt-2 mb-3">
              <h2>{{ $t("MAIL_V2.FOOTER") }}</h2>
            </div>

            <div class="form-wrapper full">
              <base-input
                :label="$t('MAIL_V2.FOOTER_TEXT')"
                :placeholder="$t('MAIL_V2.FOOTER_TEXT')"
              >
                <textarea class="form-control" v-model="settingsForm.footer_text" rows="3"></textarea>
              </base-input>
            </div>

            <div class="form-wrapper full">
              <base-input
                :label="$t('MAIL_V2.UNSUBSCRIBE_URL')"
                :placeholder="'https://...'"
                v-model="settingsForm.unsubscribe_url"
              />
            </div>

            <div class="submit-wrapper">
              <base-button
                type="button"
                class="btn btn-sm elite-submit"
                native-type="submit"
                :disabled="settingsSaving"
              >
                <i class="fas fa-spinner fa-spin" v-if="settingsSaving"></i>
                {{ $t("COMMON.SAVE") }}
              </base-button>
            </div>
          </form>
        </div>
      </tab-pane>
    </tabs>
  </div>
</template>

<script>
import swal from "sweetalert2";
import { Select, Option } from "element-ui";
import { Tabs, TabPane } from "@/components";
import { mapGetters } from "vuex";
import requestErrorMixin from "@/mixins/request-error-mixin";
import BaseInput from "@/components/Inputs/BaseInput.vue";
import MailV2Editor from "../partials/MailV2Editor.vue";

export default {
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    Tabs,
    TabPane,
    BaseInput,
    MailV2Editor,
  },

  mixins: [requestErrorMixin],

  props: {
    organizationId: { type: [String, Number], required: true },
    organization: { type: Object, default: null },
  },

  data() {
    return {
      query: "",
      filterCategory: null,
      filterStatus: null,
      loading: false,
      isEditModalOpened: false,
      editingItem: null,
      editingTemplate: null,
      renderKey: 1,
      // Settings
      settingsLoading: false,
      settingsSaving: false,
      settingsForm: {
        default_locale: "fr",
        from_name: "",
        from_email: "",
        reply_to_name: "",
        reply_to_email: "",
        logo_url: "",
        logo_height: 40,
        brand_primary_color: "#0091ca",
        brand_secondary_color: "#EF8152",
        footer_text: "",
        unsubscribe_url: "",
      },
    };
  },

  computed: {
    ...mapGetters("mailV2", { templates: "templates" }),

    filteredTemplates() {
      let list = this.templates || [];
      if (this.filterCategory) list = list.filter((t) => t.category === this.filterCategory);
      if (this.filterStatus === "customized") list = list.filter((t) => t.is_customized);
      else if (this.filterStatus === "default") list = list.filter((t) => !t.is_customized);
      if (this.query) {
        const q = this.query.toLowerCase();
        list = list.filter((t) =>
          this.getLocale(t.type_name)?.toLowerCase().includes(q) ||
          t.code?.toLowerCase().includes(q)
        );
      }
      return list;
    },
  },

  watch: {
    organizationId: {
      handler(val) { if (val) this.init(); },
      immediate: true,
    },
  },

  methods: {
    async init() {
      this.loadTemplates();
      this.loadSettings();
    },

    async loadTemplates() {
      if (!this.organizationId) return;
      this.loading = true;
      try {
        await this.$store.dispatch("mailV2/listTemplates", { orgId: this.organizationId });
      } finally { this.loading = false; }
    },

    // ---- Settings ----
    async loadSettings() {
      this.settingsLoading = true;
      try {
        const settings = await this.$store.dispatch("mailV2/getSettings", { orgId: this.organizationId });
        const org = this.organization;
        this.settingsForm = {
          default_locale: settings?.default_locale || "fr",
          from_name: settings?.from_name || org?.name || "",
          from_email: settings?.from_email || org?.email || "",
          reply_to_name: settings?.reply_to_name || "",
          reply_to_email: settings?.reply_to_email || org?.email || "",
          logo_url: settings?.logo_url || org?.logo || "",
          logo_height: settings?.logo_height || 40,
          brand_primary_color: settings?.brand_primary_color || "#0091ca",
          brand_secondary_color: settings?.brand_secondary_color || "#EF8152",
          footer_text: settings?.footer_text || "",
          unsubscribe_url: settings?.unsubscribe_url || "",
        };
      } finally { this.settingsLoading = false; }
    },

    async saveSettings() {
      this.settingsSaving = true;
      try {
        await this.$store.dispatch("mailV2/updateSettings", { orgId: this.organizationId, data: this.settingsForm });
        this.$notify({ type: "success", message: this.$t("MAIL_V2.SETTINGS_SAVED") });
      } catch (error) {
        const resp = error?.response?.data;
        if (resp?.errors && typeof resp.errors === "object" && !Array.isArray(resp.errors)) {
          // Laravel validation format: { errors: { field: [messages] } }
          const messages = Object.values(resp.errors).flat().join("\n");
          this.$notify({ type: "danger", message: resp.message || messages });
        } else {
          await this.showRequestError(error);
        }
      }
      finally { this.settingsSaving = false; }
    },

    // ---- Templates ----
    async customize(item) {
      const confirmation = await swal.fire({
        text: this.$t("MAIL_V2.CUSTOMIZE_CONFIRM", { name: this.getLocale(item.type_name) }),
        type: "question", buttonsStyling: false, showCancelButton: true,
        confirmButtonText: this.$t("MAIL_V2.CUSTOMIZE"), cancelButtonText: this.$t("COMMON.CANCEL"),
        confirmButtonClass: "btn btn-primary", cancelButtonClass: "btn btn-warning",
      });
      if (confirmation.value !== true) return;
      try {
        const template = await this.$store.dispatch("mailV2/customizeTemplate", { orgId: this.organizationId, mailTypeId: item.mail_type_id });
        this.editingItem = item;
        this.editingTemplate = template;
        this.isEditModalOpened = true;
        this.renderKey++;
        this.$notify({ type: "success", message: this.$t("MAIL_V2.TEMPLATE_CUSTOMIZED") });
        this.loadTemplates();
      } catch (error) { await this.showRequestError(error); }
    },

    editTemplate(item) {
      this.editingItem = item;
      this.editingTemplate = item.template;
      this.isEditModalOpened = true;
      this.renderKey++;
    },

    async resetToDefault(item) {
      const confirmation = await swal.fire({
        text: this.$t("MAIL_V2.RESET_CONFIRM"), type: "warning", buttonsStyling: false,
        showCancelButton: true, confirmButtonText: this.$t("MAIL_V2.RESET_DEFAULT"),
        cancelButtonText: this.$t("COMMON.CANCEL"),
        confirmButtonClass: "btn btn-danger", cancelButtonClass: "btn btn-warning",
      });
      if (confirmation.value !== true) return;
      try {
        await this.$store.dispatch("mailV2/deleteTemplate", { orgId: this.organizationId, templateId: item.template.id });
        this.$notify({ type: "success", message: this.$t("MAIL_V2.TEMPLATE_RESET") });
        this.loadTemplates();
      } catch (error) { await this.showRequestError(error); }
    },

    closeModal() { this.isEditModalOpened = false; this.editingItem = null; this.editingTemplate = null; },
    onSaved() { this.closeModal(); this.loadTemplates(); this.$notify({ type: "success", message: this.$t("MAIL_V2.TEMPLATE_SAVED") }); },

    getLocale(val) {
      if (!val) return "";
      if (typeof val === "string") return val;
      return val[this.$i18n?.locale || "fr"] || val.fr || val.en || Object.values(val)[0] || "";
    },
  },
};
</script>

<style scoped>
.mail-type-cards { display: flex; flex-direction: column; gap: 10px; }
.mail-type-card { display: flex; align-items: center; gap: 16px; padding: 14px 18px; border: 1px solid #e9ecef; border-radius: 8px; background: #fff; transition: box-shadow 0.15s; }
.mail-type-card:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
.mail-type-card-header { flex: 1; }
.mail-type-card-title { margin: 0 0 4px; font-size: 14px; font-weight: 600; color: #333; }
.mail-type-card-code { font-size: 11px; font-family: monospace; color: #999; }
.mail-type-card-badges { display: flex; gap: 6px; margin-top: 4px; }
.badge-category { padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: 600; text-transform: uppercase; }
.badge-category--transactional { background: #e3f2fd; color: #1565c0; }
.badge-category--notification { background: #fff3e0; color: #e65100; }
.badge-category--marketing { background: #f3e5f5; color: #7b1fa2; }
.badge-status { padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: 600; }
.badge-status--custom { background: #e8f5e9; color: #2e7d32; }
.badge-status--default { background: #f5f5f5; color: #757575; }
.mail-type-card-locales { display: flex; gap: 4px; }
.locale-chip { padding: 2px 8px; background: #f0f0f0; border-radius: 4px; font-size: 11px; font-weight: 700; color: #666; }
.mail-type-card-actions { display: flex; gap: 6px; flex-shrink: 0; }

</style>
